import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/AcoesMarketing_ListaInput';
import '../contracts/AcoesMarketing_ListaResponse';
import '../contracts/AcoesMarketing_GetResponse';
import '../contracts/AcoesMarketing_SalvarResponse';
import '../contracts/AcoesMarketing_SalvarInput';
import '../contracts/AcoesMarketing_GaleriaFotoInput';
import '../contracts/AcoesMarketing_ArquivarInput';
import '../contracts/AcoesMarketing_RestaurarInput';
import '../contracts/AcoesMarketing_UploadFotoInput';
import '../contracts/AcoesMarketing_UploadFotoResponse';
import type { AcoesMarketing_SalvarInput } from '../contracts/AcoesMarketing_SalvarInput';
import type { AcoesMarketing_ListaInput } from '../contracts/AcoesMarketing_ListaInput';
import type { AcoesMarketing_AcaoRow } from '../contracts/AcoesMarketing_AcaoRow';

/** The ENUM of acoes_marketing.acao_tipo — mirrors AcoesMarketing_SalvarInput::TIPOS (PHP). */
export const ACOES_MARKETING_TIPOS = ['influencer', 'acao_social', 'evento', 'midia_paga', 'parceria', 'outro'] as const;
export type AcaoMarketingTipo = typeof ACOES_MARKETING_TIPOS[number];
/** Tipos que o PEX conta pela URL da publicação — mirrors AcoesMarketing_SalvarInput::TIPOS_EXIGEM_LINK (PHP). */
export const ACOES_MARKETING_TIPOS_EXIGEM_LINK: readonly string[] = ['influencer', 'acao_social'];

/**
 * Composable for "Ações de Marketing" (/marketing/acoes — Full, Franqueado,
 * Gerente). Mirrors useSiteUnidades.ts; the list is scoped by the user's
 * franchise filter on the backend and only NARROWED by the filters sent here.
 */
export function useAcoesMarketing() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /** Fetch the actions the user may see, optionally narrowed by período/franquia/tipo. */
  const fetchAcoes = async (filtros: AcoesMarketing_ListaInput = {}): Promise<AcoesMarketing_AcaoRow[]> => {
    log('useAcoesMarketing', 'fetchAcoes', 'Fetching actions', filtros);
    isLoading.value = true;
    error.value = null;

    try {
      const params: Record<string, string> = {};
      for (const [k, v] of Object.entries(filtros)) {
        if (v !== undefined && v !== null && String(v) !== '') params[k] = String(v);
      }
      const response = await httpService.get('/index.php/marketing/acoes/lista', {
        params,
        responseContract: 'AcoesMarketing_ListaResponse',
        inputContract: 'AcoesMarketing_ListaInput'
      });
      log('useAcoesMarketing', 'fetchAcoes', 'Response', response);

      if (response.success) {
        return (response.dados?.items || []) as AcoesMarketing_AcaoRow[];
      }
      throw new Error(response.error || 'Erro ao buscar ações de marketing');
    } catch (err) {
      logError('useAcoesMarketing', 'fetchAcoes', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** Fetch a single action by id. */
  const fetchAcao = async (acaoId: number): Promise<AcoesMarketing_AcaoRow | undefined> => {
    log('useAcoesMarketing', 'fetchAcao', 'Fetching action', acaoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/marketing/acoes/get/${acaoId}`, { responseContract: 'AcoesMarketing_GetResponse' });
      log('useAcoesMarketing', 'fetchAcao', 'Response', response);

      if (response.success) {
        return response.dados?.acao as AcoesMarketing_AcaoRow | undefined;
      }
      throw new Error(response.error || 'Erro ao buscar ação de marketing');
    } catch (err) {
      logError('useAcoesMarketing', 'fetchAcao', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** Create or update (id present = update). */
  const salvarAcao = async (data: AcoesMarketing_SalvarInput) => {
    log('useAcoesMarketing', 'salvarAcao', 'Saving action', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/marketing/acoes/salvar', data, {
        responseContract: 'AcoesMarketing_SalvarResponse',
        inputContract: 'AcoesMarketing_SalvarInput'
      });
      log('useAcoesMarketing', 'salvarAcao', 'Response', response);

      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar ação de marketing');
    } catch (err) {
      logError('useAcoesMarketing', 'salvarAcao', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** Soft delete (archive). */
  const arquivarAcao = async (acaoId: number) => {
    log('useAcoesMarketing', 'arquivarAcao', 'Archiving action', acaoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/marketing/acoes/arquivar/${acaoId}`, {}, {
        responseContract: 'AcoesMarketing_SalvarResponse',
        inputContract: 'AcoesMarketing_ArquivarInput'
      });
      log('useAcoesMarketing', 'arquivarAcao', 'Response', response);

      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao arquivar ação de marketing');
    } catch (err) {
      logError('useAcoesMarketing', 'arquivarAcao', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** Restore a previously archived action. */
  const restaurarAcao = async (acaoId: number) => {
    log('useAcoesMarketing', 'restaurarAcao', 'Restoring action', acaoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/marketing/acoes/restaurar/${acaoId}`, {}, {
        responseContract: 'AcoesMarketing_SalvarResponse',
        inputContract: 'AcoesMarketing_RestaurarInput'
      });
      log('useAcoesMarketing', 'restaurarAcao', 'Response', response);

      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao restaurar ação de marketing');
    } catch (err) {
      logError('useAcoesMarketing', 'restaurarAcao', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** Upload one photo (multipart, field `foto`) and return its public URL. */
  const uploadFoto = async (file: File): Promise<string> => {
    log('useAcoesMarketing', 'uploadFoto', 'Uploading photo', { name: file.name, size: file.size, type: file.type });
    isLoading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('foto', file);

      const response = await httpService.post('/index.php/marketing/acoes/upload_foto', formData, {
        responseContract: 'AcoesMarketing_UploadFotoResponse',
        inputContract: 'AcoesMarketing_UploadFotoInput'
      });
      log('useAcoesMarketing', 'uploadFoto', 'Response', response);

      if (response.success && response.dados) {
        return response.dados.url;
      }
      throw new Error(response.error || 'Erro ao enviar a foto');
    } catch (err) {
      logError('useAcoesMarketing', 'uploadFoto', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAcoes,
    fetchAcao,
    salvarAcao,
    arquivarAcao,
    restaurarAcao,
    uploadFoto
  };
}
